import intl from 'react-intl-universal';
import { useState, useEffect, useRef } from 'react';

import {
    SettingOutlined,
    FormOutlined,
    RedoOutlined,
    ColumnHeightOutlined,
} from '@ant-design/icons';
import {
    ProTableProps,
    ProTable,
    ProColumnType,
} from '@ant-design/pro-components';
import { Dropdown } from 'antd';
import { default as Button } from '@/components/CssBtn';

import { FormItemValueType, FormConfigProvider } from '../dynamicForm';

import Setting from './options/setting';
import FilterSetting from './options/filterSetting';
import { getDiyList } from '@/api/common';
import { PRO_TABLE_CONFIG } from '@/commonConfig/componentConfig';

import type { DiyFieldItem, DynamicTableType } from '@/type/dynamicTable';

import style from './index.less';
import {
    clearFilterColumns,
    clearColumns,
    getAllFilterColumns,
} from './utils/index';
import { isBoolean, isObject } from 'lodash';

interface Props extends ProTableProps<any, any, FormItemValueType> {
    tableType?: DynamicTableType; // 表格key ，用于存储表格列配置; 需要自定义列 时必填
    onRload?: () => void;
}

const DynamicTable: React.FC<Props> = (props) => {
    const {
        tableType,
        columns: originColumns,
        toolBarRender,
        toolbar,
        search,
        options = true,
        size: defaultSize = 'middle',
        actionRef: originActionRef,
        ...otherProps
    } = props;
    const [filterColumns, setFilterColumns] = useState<
        ProColumnType<any, FormItemValueType>[]
    >([]);
    const [showSetting, setShowSetting] = useState<boolean>(false);
    const [showFilterSetting, setShowFilterSetting] = useState<boolean>(false);
    const [showFields, setShowFields] = useState<DiyFieldItem[]>([]);
    const [columns, setColumns] = useState(originColumns || []);
    // 是否显示的是全部的字段
    // const [isShowAllField, setIsShowAllField] = useState(false);

    // 自定义筛选
    const [isShowFilterSettingBtn, setIsShowFilterSettingBtn] =
        useState<boolean>(false);
    const [selectFields, setSelectFields] = useState<DiyFieldItem[]>([]);

    // options
    const [size, setSize] = useState<ProTableProps['size']>(defaultSize);
    const ref = useRef();

    const actionRef = originActionRef || ref;

    useEffect(() => {
        init();
    }, [originColumns]);

    const init = () => {
        if (!tableType) {
            // console.warn('DynamicTable: 需要[自定义列]时，请指定 tableType');
            // 更新columns
            setColumns(originColumns || []);
            return;
        }

        getDiyList({ type: tableType }).then((res = {}) => {
            setShowFields(res.showFields || []);
            setSelectFields(res.selectFields || []);
            const tableColumns = clearColumns(
                originColumns,
                res?.showFields || [],
            );
            setColumns(tableColumns);
            // setIsShowAllField(tableColumns.length === originColumns?.length);

            // console.log(tableColumns.length === originColumns?.length);

            setFilterColumns(
                clearFilterColumns(originColumns, res?.selectFields || []),
            );

            setIsShowFilterSettingBtn(
                getAllFilterColumns(originColumns)?.length > 3,
            );
        });
    };

    const onShowSetting = () => {
        setShowSetting(true);
    };

    const reload = () => {
        if (props.onRload) {
            props.onRload?.();
        } else {
            actionRef.current?.reload();
        }
    };

    const getOptions = (options) => {
        let showSettingBtn = false;
        let showReloadBtn = false;
        let showDensityBtn = false;

        if (options) {
            if (isBoolean(options)) {
                showSettingBtn = !!tableType;
                showReloadBtn = true;
                showDensityBtn = true;
            }

            if (isObject(options)) {
                if (options.setting && tableType) {
                    showSettingBtn = true;
                }
                if (options.reload) {
                    showReloadBtn = true;
                }
                if (options.density) {
                    showDensityBtn = true;
                }
            }
        }

        return [
            showReloadBtn && (
                <RedoOutlined
                    className={style.toolBtn}
                    key="reload"
                    onClick={reload}
                />
            ),

            showDensityBtn && (
                <Dropdown
                    menu={{
                        selectable: true,
                        selectedKeys: [size],
                        onClick: ({ key }) => {
                            setSize(key);
                        },
                        style: {
                            width: 80,
                        },
                        items: [
                            {
                                key: 'large',
                                label: intl.get('默认'),
                            },
                            {
                                key: 'middle',
                                label: intl.get('中等'),
                            },
                            {
                                key: 'small',
                                label: intl.get(' 紧凑'),
                            },
                        ],
                    }}
                    placement="bottomLeft"
                    arrow
                >
                    <ColumnHeightOutlined
                        className={style.toolBtn}
                        key="density"
                    />
                </Dropdown>
            ),

            showSettingBtn && (
                <SettingOutlined
                    key="setting"
                    className={style.toolBtn}
                    onClick={onShowSetting}
                />
            ),
        ].filter((item) => !!item);
    };

    const renderToolBarRender = (action: any, ...rest: any[]) => {
        let originToolBar: React.ReactNode[] = [];

        if (toolBarRender === false) return false;

        if (toolBarRender) {
            originToolBar = toolBarRender?.(action, ...rest) || [];
        }

        return [...originToolBar, ...getOptions(options)].filter(
            (item) => !!item,
        );
    };

    const onSaveColSetting = (showFields: DiyFieldItem[]) => {
        setColumns(clearColumns(originColumns, showFields));
        setShowFields(showFields);
        setShowSetting(false);
        // setIsShowAllField(showFields.length === originColumns?.length);
    };

    const onFilterSettingSave = (selectFields: DiyFieldItem[]) => {
        // console.log(fields);
        setFilterColumns(clearFilterColumns(originColumns, selectFields));
        setSelectFields(selectFields);
        setShowFilterSetting(false);
    };

    return (
        <FormConfigProvider>
            <ProTable
                columns={[...filterColumns, ...columns]}
                options={false}
                toolBarRender={renderToolBarRender}
                size={size}
                actionRef={actionRef}
                toolbar={
                    toolbar
                        ? {
                              ...toolbar,
                              actions: [
                                  ...(toolbar.actions || []),
                                  ...getOptions(options),
                              ],
                          }
                        : undefined
                }
                {...PRO_TABLE_CONFIG(1300)}
                search={
                    (search || search === undefined) && {
                        ...(search || {}),
                        // filterType: 'query',
                        // showHiddenNum: true,
                        // defaultCollapsed: false,
                        optionRender: (searchConfig, formProps, dom) => {
                            const customReader = props.search?.optionRender?.(
                                searchConfig,
                                formProps,
                                dom,
                            );

                            return isShowFilterSettingBtn
                                ? [
                                      <Button
                                          key="filter"
                                          type="link"
                                          icon={<FormOutlined />}
                                          style={{ marginRight: 5 }}
                                          onClick={() =>
                                              setShowFilterSetting(true)
                                          }
                                      >
                                          {intl.get('自定义查询')}
                                      </Button>,
                                      ...(customReader || dom),
                                  ]
                                : customReader || dom;
                        },
                    }
                }
                sticky={{
                    offsetHeader: 0,
                }}
                {...otherProps}
            ></ProTable>

            {showSetting && tableType && (
                <Setting
                    showFields={showFields}
                    columns={
                        originColumns?.filter(
                            (col) => !col.hideInTable && col.dataIndex,
                        ) || []
                    }
                    show={showSetting}
                    // isShowAllField={isShowAllField}
                    tableType={tableType}
                    onSuccess={onSaveColSetting}
                    onClose={() => setShowSetting(false)}
                ></Setting>
            )}

            {showFilterSetting && (
                <FilterSetting
                    show={showFilterSetting}
                    tableType={tableType}
                    columns={
                        originColumns?.filter(
                            (col) => !col.hideInSearch && col.dataIndex,
                        ) || []
                    }
                    showFields={selectFields}
                    onSuccess={onFilterSettingSave}
                    onClose={() => setShowFilterSetting(false)}
                />
            )}
        </FormConfigProvider>
    );
};

export default DynamicTable;
